import { memo, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { Stack } from '@mui/material';
import { LoadingMessage } from '@client/components/common/LoadingMessage';
import { useFirestore } from '@client/hooks/utils.hooks';
import { getBeaconById } from '@client/selectors/entities/beacons';
import { addBeacon, startAddBeaconImpression } from '@client/slices/entities/beacons.reducer';
import { useDispatch, useListeners, useSelector } from '@client/hooks/redux.hooks';
import { getSessionUserState } from '@client/selectors/helpers/login';
import { hasFirebaseScaffolded } from '@client/selectors/helpers/clients';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import { ShowCall } from './ShowCall';
import { NearbyShowCalls } from './NearbyShowCalls';
import { connectDetailsListeners } from './connect-details.listeners';

interface DetailsProps {
  readonly showCallId: string;
}

export const Details = memo<DetailsProps>(({ showCallId }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const userState = useSelector(getSessionUserState);
  const loaded = useSelector(hasFirebaseScaffolded);

  useListeners(connectDetailsListeners);

  const [call, isLoading] = useFirestore(
    (SDK) => SDK.Beacons.get(showCallId),
    {
      key: showCallId,
      selector: getBeaconById,
      upserter: addBeacon,
    },
  );

  useEffect(() => {
    if (userState !== null && loaded) {
      dispatch(startAddBeaconImpression(showCallId));
    }
  }, [userState, loaded, showCallId, dispatch]);

  if (isEmpty(call) || isLoading) {
    return <LoadingMessage />;
  }

  return (
    <>
      <DocumentTitle title={call.profileData.name} />

      <Stack spacing={isMobile ? 4 : 3}>
        <ShowCall {...call} />

        <div>
          <NearbyShowCalls
            id={call.id}
            latLng={call.latLng}
            profileType={call.profileType}
          />
        </div>
      </Stack>
    </>
  );
});

export default Details;
